import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

import { apiDetail } from '../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BILL-6 — a user who has not accepted the Terms of Service now in force
 * (every user who signed up before acceptance was recorded, and everyone
 * when the Terms change) is asked to review and accept them. The version
 * accepted is the one the server said is in force.
 */
export default function TermsNotice() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/legal/terms-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, [token]);

  if (!status?.needs_acceptance) return null;

  const accept = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/legal/accept-terms`,
        { terms_version: status.current_version },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStatus(r.data);
      toast.success('Thank you. Your acceptance is recorded.');
    } catch (e) {
      toast.error(apiDetail(e) || 'Could not record your acceptance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-slate-800 text-white px-4 py-2.5 text-sm flex items-center justify-center gap-3 flex-wrap"
      data-testid="terms-notice"
    >
      <FileText className="w-4 h-4 shrink-0" />
      <span className="font-medium">
        {status.accepted_version
          ? 'The Northline Terms of Service have been updated.'
          : 'Please review and accept the Northline Terms of Service.'}
      </span>
      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline opacity-90">
        Read the Terms
      </a>
      <button
        type="button"
        onClick={accept}
        disabled={saving}
        className="bg-white text-slate-900 px-3 py-1 rounded-md font-semibold text-xs hover:bg-slate-100 transition-colors disabled:opacity-60"
        data-testid="accept-terms-button"
      >
        Accept Terms
      </button>
    </div>
  );
}
